import { Extension } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { MarkType } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

/** Class the decoration adds; styled in `styles.css` beside `.cotera-comment`. */
const ACTIVE_CLASS = 'cotera-comment-active';

/**
 * The comment thread the caret currently sits in, or `null`.
 *
 * Reads the marks at the selection head rather than searching the document: the
 * `comment` mark is `inclusive: false`, so this resolves to a thread only when the
 * cursor is genuinely inside the annotated range, never when it is parked on the
 * boundary. That is the same test {@link CommentThreadPopover} uses, so the popover,
 * the decoration and the rail's expanded card always agree on which thread is active.
 */
function activeCommentId(
  state: EditorState,
  markType: MarkType
): string | null {
  const mark = state.selection.$from
    .marks()
    .find((candidate) => candidate.type === markType);
  if (mark === undefined) {
    return null;
  }
  const commentId = mark.attrs['commentId'];
  return typeof commentId === 'string' ? commentId : null;
}

/**
 * Paints every span of the *active* comment thread, so picking a card out of the
 * comments rail lands somewhere visible in the prose.
 *
 * Decorations rather than a mark attribute: which thread is active is view state that
 * changes as the caret moves, and writing it into the document would put a cursor
 * position into the saved JSON (and into every collaborator's undo stack).
 *
 * The document walk only runs when the caret is actually inside a comment, which the
 * cheap {@link activeCommentId} check establishes first — with no active thread this
 * returns before touching the doc, which is the case on essentially every keystroke.
 */
export const CommentActiveHighlight = Extension.create({
  name: 'commentActiveHighlight',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('commentActiveHighlight'),
        props: {
          decorations: (state) => {
            const markType = state.schema.marks['comment'];
            if (markType === undefined) {
              return null;
            }
            const commentId = activeCommentId(state, markType);
            if (commentId === null) {
              return null;
            }

            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isText) {
                return;
              }
              const carriesThread = node.marks.some(
                (mark) =>
                  mark.type === markType &&
                  mark.attrs['commentId'] === commentId
              );
              if (carriesThread) {
                decorations.push(
                  Decoration.inline(pos, pos + node.nodeSize, {
                    class: ACTIVE_CLASS,
                  })
                );
              }
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});
